import { Intercom, shutdown, update } from '@intercom/messenger-js-sdk';
import React, { type ReactNode, useEffect, useRef } from 'react';

import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { isExtensionContext } from '@apps/web/lib/extension_context';
import { getRuntimeConfig } from '@apps/web/lib/runtime_config';
import {
    useOrganization,
    useUser,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

const INTERCOM_LAUNCHER_VERTICAL_PADDING_PX = 40;

type IntercomProviderProps = {
    readonly children: ReactNode;
};

export function IntercomProvider({ children }: IntercomProviderProps) {
    const { data: user } = useUser({ reason: 'intercom-provider' });
    const organizationId = useOrganizationId();
    const { data: organization } = useOrganization(organizationId, {
        reason: 'intercom-provider',
    });
    const hasBooted = useRef(false);
    const previousUserId = useRef<string | null>(null);

    useEffect(() => {
        const { intercomAppId } = getRuntimeConfig();
        if (!intercomAppId || isExtensionContext()) {
            if (hasBooted.current) {
                shutdown();
                hasBooted.current = false;
            }
            return;
        }

        // Shutdown and reboot if user changed (logout or different user login)
        if (
            hasBooted.current &&
            previousUserId.current !== null &&
            previousUserId.current !== (user?.id ?? null)
        ) {
            shutdown();
            hasBooted.current = false;
        }

        const intercomConfig = user?.email
            ? {
                  app_id: intercomAppId,
                  vertical_padding: INTERCOM_LAUNCHER_VERTICAL_PADDING_PX,
                  user_id: user.id,
                  name:
                      [user.first_name, user.last_name]
                          .filter(Boolean)
                          .join(' ') || user.email,
                  email: user.email,
                  ...(organization && {
                      company: {
                          company_id: organization.id,
                          name: organization.internal_name
                              ? `${organization.name} (${organization.internal_name})`
                              : organization.name,
                      },
                  }),
              }
            : {
                  app_id: intercomAppId,
                  vertical_padding: INTERCOM_LAUNCHER_VERTICAL_PADDING_PX,
              };

        if (!hasBooted.current) {
            // First boot (anonymous or identified)
            Intercom(intercomConfig);
            hasBooted.current = true;
        } else {
            // Update existing session with new data
            update(intercomConfig);
        }

        previousUserId.current = user?.id ?? null;
    }, [user, organizationId, organization]);

    // Shutdown Intercom on unmount
    useEffect(() => {
        return () => {
            shutdown();
        };
    }, []);

    return <React.Fragment>{children}</React.Fragment>;
}
